import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { AssessmentTaskLaunchService } from "./assessment_task_launch.service";
import { assessmentTaskSessionIdentity } from "./assessment_task_session.util";
import type { AssessmentTaskRequirementRef } from "./assessment_tasks.types";
import { LoginService } from "./login.service";

export type AssessmentTaskFormContext =
    | { kind: "create"; productLicenseId: null; requirement: null }
    | { kind: "create"; productLicenseId: number; requirement: null }
    | { kind: "create"; productLicenseId: number; requirement: AssessmentTaskRequirementRef }
    | { kind: "edit"; productLicenseId: number; taskId: number };

export interface AssessmentTaskFormLaunchState {
    isOpen: boolean;
    context: AssessmentTaskFormContext | null;
}

const closedState = (): AssessmentTaskFormLaunchState => ({ isOpen: false, context: null });
const positiveId = (value: unknown): value is number =>
    typeof value === "number" && Number.isSafeInteger(value) && value > 0;

function validCanonicalKey(value: unknown): value is string {
    if (typeof value !== "string" || !value || value.trim() !== value || value.length > 510 ||
        /[\x00-\x1f\x7f]/.test(value)) return false;
    let characters = 0;
    for (const character of value) {
        const codePoint = character.codePointAt(0)!;
        if ((codePoint >= 0xd800 && codePoint <= 0xdfff) || ++characters > 255) return false;
    }
    return true;
}

@Injectable({ providedIn: "root" })
export class AssessmentTaskFormLaunchService implements OnDestroy {
    private readonly state = new BehaviorSubject<AssessmentTaskFormLaunchState>(closedState());
    private readonly subscriptions = new Subscription();
    private sessionIdentity: string | null = null;
    private activeAssessmentId: number | null = null;

    constructor(login: LoginService, taskBrowser: AssessmentTaskLaunchService) {
        this.subscriptions.add(login.getUserUpdates().subscribe((user) => {
            const identity = assessmentTaskSessionIdentity(user);
            if (identity !== this.sessionIdentity) {
                this.sessionIdentity = identity;
                this.close();
            }
        }));
        this.subscriptions.add(taskBrowser.watchState().subscribe(({ activeAssessmentId }) => {
            if (activeAssessmentId !== this.activeAssessmentId) {
                this.activeAssessmentId = activeAssessmentId;
                this.close();
            }
        }));
    }

    watchState(): Observable<AssessmentTaskFormLaunchState> {
        return this.state.asObservable();
    }

    getSnapshot(): AssessmentTaskFormLaunchState {
        return this.state.value;
    }

    openHeaderCreate(): void {
        this.open({ kind: "create", productLicenseId: null, requirement: null });
    }

    openAssessmentCreate(productLicenseId: number): void {
        this.requireAssessmentId(productLicenseId);
        this.open({ kind: "create", productLicenseId, requirement: null });
    }

    openRequirementCreate(productLicenseId: number, requirement: AssessmentTaskRequirementRef): void {
        this.requireAssessmentId(productLicenseId);
        if (!validCanonicalKey(requirement?.sectionKey) || !validCanonicalKey(requirement?.requirementKey)) {
            throw new Error("A canonical requirement is required to add a task.");
        }
        this.open({
            kind: "create", productLicenseId,
            requirement: { sectionKey: requirement.sectionKey, requirementKey: requirement.requirementKey }
        });
    }

    openCreatorEdit(productLicenseId: number, taskId: number): void {
        this.requireAssessmentId(productLicenseId);
        if (!positiveId(taskId)) throw new Error("A valid task ID is required to edit a task.");
        // The form fetches current capabilities; status-only assignees keep structural fields disabled.
        this.open({ kind: "edit", productLicenseId, taskId });
    }

    close(): void {
        if (this.state.value.isOpen) this.state.next(closedState());
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.state.complete();
    }

    private open(context: AssessmentTaskFormContext): void {
        if (!this.sessionIdentity) {
            this.close();
            return;
        }
        this.state.next({ isOpen: true, context });
    }

    private requireAssessmentId(productLicenseId: number): void {
        if (!positiveId(productLicenseId)) throw new Error("A valid assessment ID is required.");
    }
}
